import type { VisibilityState } from '@tanstack/react-table';
import type { TFunction } from 'i18next';

export type GrantsView = 'list' | 'audit';

// Column ids of the editable audit-lifecycle columns (match the accessor keys
// in access-grants-table.tsx). Hidden in List view, shown in Audit view.
export const AUDIT_COLUMN_IDS = [
  'grantee',
  'reviewDecision',
  'reviewComment',
  'nextReviewAt',
] as const;

// Columns hidden by default in the original List view (verbose reference
// fields the user can opt back in via the column picker).
const REFERENCE_DEFAULT_HIDDEN = [
  'resourceAccountId',
  'principalAccountId',
  'resourceRegion',
  'service',
  'firstObservedAt',
];

// Reference columns Audit view drops to make room for the audit columns. The
// review workflow needs the four editable columns on screen without horizontal
// scrolling, so everything not needed to make a decision goes.
const AUDIT_HIDDEN_REFERENCE = [
  'mechanism',
  'principalType',
  'grantOrigin',
  'grantScope',
  'trustStatus',
  'assetType',
];

// A VisibilityState only needs the hidden columns (false); everything absent
// is visible. Switching views therefore reveals whatever the other view hid.
function hidden(ids: string[]): VisibilityState {
  return Object.fromEntries(ids.map((id) => [id, false]));
}

export type GrantsViewDef = {
  id: GrantsView;
  label: string;
  description: string;
  columnVisibility: VisibilityState;
};

const VIEW_COLUMNS: Pick<GrantsViewDef, 'id' | 'columnVisibility'>[] = [
  {
    id: 'list',
    columnVisibility: hidden([
      ...AUDIT_COLUMN_IDS,
      ...REFERENCE_DEFAULT_HIDDEN,
    ]),
  },
  {
    id: 'audit',
    columnVisibility: hidden([
      ...AUDIT_HIDDEN_REFERENCE,
      ...REFERENCE_DEFAULT_HIDDEN,
    ]),
  },
];

/** What each view is called and what it shows. */
const viewText = (
  t: TFunction<'accessGrants'>,
): Record<GrantsView, Pick<GrantsViewDef, 'label' | 'description'>> => ({
  list: { label: t('list'), description: t('defaultInventoryColumns') },
  audit: { label: t('accessReview'), description: t('manageReviewColumns') },
});

export const grantsViews = (t: TFunction<'accessGrants'>): GrantsViewDef[] =>
  VIEW_COLUMNS.map((view) => ({ ...view, ...viewText(t)[view.id] }));

/**
 * Whether to force the default view. The Access review preset also comes from
 * the URL hash, so a shared #access-review link would otherwise land a caller
 * with no AccessGrant:Review on a grid of inputs that cannot save.
 *
 * Waits for entitlements: `can` is false while they resolve, and clamping then
 * would drop a genuine reviewer back to List on every page load.
 */
export function shouldClampGrantsView({
  view,
  canReview,
  isLoading,
}: {
  view: GrantsView | null;
  canReview: boolean;
  isLoading: boolean;
}): boolean {
  return !isLoading && !canReview && view === 'audit';
}

/**
 * The views to offer. Access review is the only one whose columns are editable
 * -- grantee, decision and comment all write through
 * PATCH /api/core/access/grants/{id}, which requires AccessGrant:Review. That
 * action is in the triage group, not the view-integration-data one, so a
 * read-only caller was being shown a grid of inputs whose every save would
 * 403. The server still scopes each write by integration; this only decides
 * whether the view is offered at all.
 */
export function grantsViewsFor(
  t: TFunction<'accessGrants'>,
  { canReview }: { canReview: boolean },
): GrantsViewDef[] {
  const views = grantsViews(t);
  return canReview ? views : views.filter((view) => view.id !== 'audit');
}

export const DEFAULT_GRANTS_VIEW: GrantsView = 'list';

export function viewColumnVisibility(view: GrantsView): VisibilityState {
  return (
    VIEW_COLUMNS.find((v) => v.id === view)?.columnVisibility ??
    VIEW_COLUMNS[0].columnVisibility
  );
}

// The set of columns currently hidden (value === false). Columns absent from
// the map are visible, so only explicit `false` entries count.
function hiddenSet(visibility: VisibilityState): Set<string> {
  return new Set(
    Object.entries(visibility)
      .filter(([, visible]) => visible === false)
      .map(([id]) => id),
  );
}

// Which preset the current column visibility matches, or null once the user
// has toggled columns away from any preset (so no preset stays highlighted).
export function matchGrantsView(
  visibility: VisibilityState,
): GrantsView | null {
  const current = hiddenSet(visibility);
  for (const view of VIEW_COLUMNS) {
    const preset = hiddenSet(view.columnVisibility);
    if (
      preset.size === current.size &&
      [...preset].every((id) => current.has(id))
    ) {
      return view.id;
    }
  }
  return null;
}
